/**
 * Setup del Giro a 8: niente coppie fisse, conta il NUMERO di ogni giocatore
 * (1..8 negli schemi di Fabio, con 1 o 2 istruttori in campo al posto dei giocatori).
 * Tocca due giocatori per scambiarne il numero.
 */
import { useState } from "react";
import { ArrowUpDown, Ellipsis, GraduationCap, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GaraState, Player } from "@/lib/gare";
import { lvl } from "./ui";

export function GiroLineup({ state, onOrder, onPlayerMenu, onRestore }: {
  state: GaraState;
  onOrder: (ids: number[]) => void;
  onPlayerMenu: (p: Player) => void;
  onRestore: (p: Player) => void;
}) {
  const [sel, setSel] = useState<number | null>(null);
  const players = state.players.filter((p) => p.role === "player")
    .sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99) || a.id - b.id);
  const instr = state.players.filter((p) => p.role === "instructor");
  const fakes = state.players.filter((p) => p.role === "fake");
  const need = 8 - instr.length;
  const ok = players.length === need && instr.length <= 2;

  const tap = (pid: number) => {
    if (sel === null) return setSel(pid);
    if (sel !== pid) {
      const ids = players.map((p) => p.id);
      const i = ids.indexOf(sel);
      const j = ids.indexOf(pid);
      [ids[i], ids[j]] = [ids[j], ids[i]];
      onOrder(ids);
    }
    setSel(null);
  };

  return (
    <div className="space-y-4">
      <div className={cn("rounded-xl border p-3 text-sm", ok ? "border-moving-200 bg-moving-50 text-moving-800" : "border-amber-200 bg-amber-50 text-amber-800")}>
        In campo servono 8 persone: <b>{players.length}</b> giocatori su {need}
        {instr.length ? <> + <b>{instr.length}</b> istruttor{instr.length === 1 ? "e" : "i"}</> : null}.
        {" "}Con 1 o 2 istruttori si usano gli schemi di Fabio con istruttori (dal menu <Ellipsis className="inline h-3.5 w-3.5" /> di un giocatore).
      </div>

      <div>
        <p className="mb-2 text-xs text-slate-500">
          <ArrowUpDown className="mr-1 inline h-3.5 w-3.5" />Il numero decide le rotazioni dello schema. Tocca due giocatori per scambiarne il numero.
        </p>
        <ol className="grid gap-1.5 sm:grid-cols-2">
          {players.map((p, i) => (
            <li key={p.id} className={cn("flex items-center gap-2 rounded-lg border bg-white px-2 py-1.5",
              sel === p.id ? "border-moving-500 ring-2 ring-moving-500" : "border-slate-200")}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-court-500 text-sm font-bold text-white">{i + 1}</span>
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => tap(p.id)}>
                <span className="block truncate text-sm font-medium text-slate-900">{p.name}</span>
                <span className="block text-xs text-slate-500">{p.level !== null ? `liv. ${lvl(p.level)}` : "livello ?"}</span>
              </button>
              <button type="button" aria-label={`Opzioni ${p.name}`} onClick={() => onPlayerMenu(p)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
                <Ellipsis className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ol>
      </div>

      {instr.length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 text-sm font-semibold text-slate-900">Istruttori in campo ({instr.length})</div>
          <div className="flex flex-wrap gap-2">
            {instr.map((p) => (
              <button key={p.id} type="button" onClick={() => onPlayerMenu(p)}
                className="flex items-center gap-1.5 rounded-lg border border-court-500/30 bg-sky-50 px-2.5 py-1.5 text-sm text-court-700">
                <GraduationCap className="h-4 w-4" /> {p.name}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">Gli istruttori giocano ma non entrano in classifica e non prendono punti.</p>
        </div>
      ) : null}

      {fakes.length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 text-sm font-semibold text-slate-900">Finti / non giocano ({fakes.length})</div>
          <div className="flex flex-wrap gap-2">
            {fakes.map((p) => (
              <button key={p.id} type="button" onClick={() => onRestore(p)}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-500 line-through hover:bg-slate-50">
                <UserCheck className="h-4 w-4" /> {p.name}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">Tocca un nome per rimetterlo in gioco.</p>
        </div>
      ) : null}
    </div>
  );
}
